import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { PlacePredictionDto } from '@fleet-beat/api';
import {
  AddressDto,
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  PageableListV2Dto,
  PlaceByCoordinatesDto,
  ResponseDto,
} from '@fleet-beat/core';
import { DataAutocompleteStorageParams } from '@fleet-beat/editor';
import { Observable, catchError, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PlacesApiService {
  sessiontoken: string;
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/places`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(
    data: DataAutocompleteStorageParams<object>,
    auth: AuthHeaders,
  ): Observable<PageableListV2Dto<PlacePredictionDto>> {
    const params = {
      ...data.params,
      sessiontoken: this.sessiontoken ? this.sessiontoken : undefined,
    };
    return this.baseApiService.get(`${this.baseUrl}/autocomplete`, { auth, params: params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<{ predictions: PlacePredictionDto[]; sessiontoken: string }>) =>
        this.baseApiService.mapResponseV2(response),
      ),
      map((response: ResponseDto<{ predictions: PlacePredictionDto[]; sessiontoken: string }>) => {
        this.sessiontoken = response.data.sessiontoken;
        return {
          data: response.data.predictions,
        };
      }),
    );
  }

  getOne(placeId: string, auth: AuthHeaders): Observable<AddressDto> {
    const params = {
      placeId,
      sessiontoken: this.sessiontoken ? this.sessiontoken : undefined,
    };
    return this.baseApiService.get(`${this.baseUrl}/details`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<AddressDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getNearestLocation(body: { lat: number; lon: number }, auth: AuthHeaders): Observable<PlaceByCoordinatesDto> {
    const params = {
      location: `${body.lat},${body.lon}`,
      sessiontoken: this.sessiontoken ? this.sessiontoken : undefined,
    };
    return this.baseApiService.get(`${this.baseUrl}/nearest`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<PlaceByCoordinatesDto>) => this.baseApiService.mapResponse(response)),
    );
  }
}
